
import React, { useRef } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { TableConfig } from '../types';
import { RotateIcon, UsersIcon, AlertTriangleIcon } from './icons';

interface TableItemProps {
  table: TableConfig;
  pixelScale: number;
  onDrag: (id: string, x: number, y: number) => void;
  onRotate: (id: string) => void;
  onOpenGuestModal: (id: string) => void;
  isSelected: boolean;
  onSelect: (id: string | null) => void;
  venueWidthPx: number;
  venueHeightPx: number;
  theme: 'light' | 'dark';
}

const TableItem: React.FC<TableItemProps> = ({ 
  table, pixelScale, onDrag, onRotate, onOpenGuestModal, isSelected, onSelect, venueWidthPx, venueHeightPx, theme
}) => {
  const draggableRef = useRef<HTMLDivElement>(null);
  
  const contentWidthPx = table.width * pixelScale;
  const contentHeightPx = table.height * pixelScale;

  const draggableContainerWidthPx = table.orientation === 'vertical' ? contentHeightPx : contentWidthPx;
  const draggableContainerHeightPx = table.orientation === 'vertical' ? contentWidthPx : contentHeightPx;

  const position = {
    x: table.x * pixelScale - draggableContainerWidthPx / 2,
    y: table.y * pixelScale - draggableContainerHeightPx / 2,
  };
  
  const handleDrag = (e: DraggableEvent, data: DraggableData) => {
    const newCenterX = (data.x + draggableContainerWidthPx / 2) / pixelScale;
    const newCenterY = (data.y + draggableContainerHeightPx / 2) / pixelScale;
    onDrag(table.id, newCenterX, newCenterY);
  };

  const clearanceDisplayWidthPx = table.clearanceWidth ? table.clearanceWidth * pixelScale : (table.clearanceRadius || 0) * 2 * pixelScale;
  const clearanceDisplayHeightPx = table.clearanceHeight ? table.clearanceHeight * pixelScale : (table.clearanceRadius || 0) * 2 * pixelScale;
  const clearanceBorderRadius = table.shape === 'round' ? '50%' : '0.25rem';

  const opacityClass = table.isGhost ? 'opacity-50 grayscale' : 'opacity-100';
  const ghostBorderClass = table.isGhost ? 'border-2 border-red-500 border-dashed' : '';

  const tableBaseColor = table.isGhost 
    ? (theme === 'dark' ? 'bg-red-900/50' : 'bg-red-200/50') 
    : table.isMainTable
      ? (theme === 'dark' ? 'bg-purple-600' : 'bg-purple-500')
      : table.shape === 'living'
        ? (theme === 'dark' ? 'bg-teal-600' : 'bg-teal-500')
        : (theme === 'dark' ? 'bg-hba-accent-purple' : 'bg-hba-accent-purple');
      
  const textColor = theme === 'dark' ? 'text-white' : 'text-white';
  const clearanceBorderColor = table.isGhost ? 'border-red-500' : (theme === 'dark' ? 'border-hba-light-purple' : 'border-gray-400');
  
  const tableBorderClass = table.isMainTable 
    ? (theme === 'dark' ? 'border-2 border-yellow-300' : 'border-2 border-yellow-400') 
    : (table.isGhost ? ghostBorderClass : 'border border-white/20');

  const showRotateButton = isSelected && (table.shape === 'rectangular' || table.shape === 'square' || table.isMainTable || table.shape === 'living');
  const showGuestListButton = isSelected;

  const handleRotateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRotate(table.id);
  };

  const handleGuestListClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenGuestModal(table.id);
  };
  
  const itemLabel = table.shape === 'living' ? 'Este living' : 'Esta mesa';
  const guestTooltip = table.isGhost 
    ? `¡Sin espacio seguro! ${itemLabel} excede la capacidad recomendada.`
    : (table.guestList && table.guestList.length > 0 
        ? `${table.guestList.length} invitado(s) nombrados / Capacidad ${table.seats}`
        : `${table.assignedSeats} asientos asignados / Capacidad ${table.seats}`);

  const renderChairs = () => {
    const chairs = [];
    const seatCount = table.seats;
    const chairSize = Math.max(4, pixelScale * 0.25); 
    const chairDistance = 3; 
    const chairColor = table.isGhost ? 'bg-red-500' : (theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500');

    if (table.shape === 'round') {
      const radius = (contentWidthPx / 2) + chairDistance;
      const angleStep = (2 * Math.PI) / seatCount;
      
      for (let i = 0; i < seatCount; i++) {
        const angle = i * angleStep;
        const top = (contentHeightPx / 2) + radius * Math.sin(angle) - (chairSize/2);
        const left = (contentWidthPx / 2) + radius * Math.cos(angle) - (chairSize/2);
        chairs.push(
          <div
            key={i}
            className={`absolute rounded-full ${chairColor}`}
            style={{ top, left, width: chairSize, height: chairSize }}
          />
        );
      }
    } else {
        // Lógica para mesas rectangulares, cuadradas, livings y MESA PRINCIPAL
        
        if (table.isMainTable) {
            // ESTILO JURADO: Todas las sillas en un solo lado (Borde Superior)
            const spacePerChair = contentWidthPx / (seatCount + 1);
            
            for (let i = 0; i < seatCount; i++) {
                const leftPos = (spacePerChair * (i + 1)) - (chairSize / 2);
                chairs.push(
                    <div 
                        key={`main-${i}`} 
                        className={`absolute rounded-full ${chairColor}`} 
                        style={{ 
                            top: -chairDistance - chairSize, // Posicionadas arriba de la mesa
                            left: leftPos, 
                            width: chairSize, 
                            height: chairSize 
                        }} 
                    />
                );
            }
        } else {
            // Lógica Estándar: Distribución en 4 lados
            const perSide = Math.ceil(seatCount / 4); 
            for(let i=0; i<perSide; i++) {
                chairs.push(<div key={`t${i}`} className={`absolute rounded-full ${chairColor}`} style={{ top: -chairDistance - chairSize, left: (contentWidthPx/(perSide+1))*(i+1) - chairSize/2, width: chairSize, height: chairSize }} />);
                chairs.push(<div key={`b${i}`} className={`absolute rounded-full ${chairColor}`} style={{ bottom: -chairDistance - chairSize, left: (contentWidthPx/(perSide+1))*(i+1) - chairSize/2, width: chairSize, height: chairSize }} />);
            }
            const remaining = Math.max(0, seatCount - (perSide*2));
            const perSideV = Math.ceil(remaining / 2);
            for(let i=0; i<perSideV; i++) {
                chairs.push(<div key={`l${i}`} className={`absolute rounded-full ${chairColor}`} style={{ left: -chairDistance - chairSize, top: (contentHeightPx/(perSideV+1))*(i+1) - chairSize/2, width: chairSize, height: chairSize }} />);
                chairs.push(<div key={`r${i}`} className={`absolute rounded-full ${chairColor}`} style={{ right: -chairDistance - chairSize, top: (contentHeightPx/(perSideV+1))*(i+1) - chairSize/2, width: chairSize, height: chairSize }} />);
            }
        }
    }
    return chairs;
  };

  return (
    <Draggable
      position={position}
      onStop={handleDrag} 
      bounds={{ 
        left: 0, 
        top: 0, 
        right: venueWidthPx - draggableContainerWidthPx, 
        bottom: venueHeightPx - draggableContainerHeightPx 
      }}
      nodeRef={draggableRef}
      cancel=".no-drag-table-action" 
    >
      <div
        ref={draggableRef} 
        style={{
          width: `${draggableContainerWidthPx}px`,
          height: `${draggableContainerHeightPx}px`,
          position: 'absolute',
          touchAction: 'none', 
        }}
        className={`cursor-grab ${isSelected ? 'table-item-selected z-30' : 'z-10'} group ${opacityClass}`}
        onClick={(e) => { e.stopPropagation(); onSelect(table.id); }}
        title={guestTooltip}
      >
        {/* Inner div for rotation and content */}
        <div
          style={{
            width: `${contentWidthPx}px`, 
            height: `${contentHeightPx}px`,
            transform: `translate(-50%, -50%) ${table.orientation === 'vertical' ? 'rotate(90deg)' : 'rotate(0deg)'}`,
            transformOrigin: 'center center',
            position: 'absolute', 
            top: '50%', 
            left: '50%',
            borderRadius: table.shape === 'round' ? '50%' : '0.25rem',
          }}
          className={`relative flex flex-col items-center justify-center shadow-lg ${tableBorderClass}`}
        >
            {/* Chairs Render */}
            {renderChairs()}
            
            {/* Table Body */}
            <div className={`absolute inset-0 ${tableBaseColor} ${textColor} flex flex-col items-center justify-center text-sm sm:text-base font-semibold z-10 overflow-hidden`} style={{ borderRadius: table.shape === 'round' ? '50%' : '0.25rem' }}>
                 {table.isGhost && <AlertTriangleIcon className="w-4 h-4 text-red-500 bg-white rounded-full p-0.5 mb-0.5" />}
                 <span>{table.displayId}</span>
                 {table.shape === 'living' && <span className="text-[8px] md:text-[10px] mt-0.5 opacity-80">LIVING</span>}
            </div>

          <div
            style={{
              width: `${clearanceDisplayWidthPx}px`,
              height: `${clearanceDisplayHeightPx}px`,
              borderRadius: clearanceBorderRadius,
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)', 
            }}
            // Cambio aquí: border-2 a border (1px) para hacerla más delgada
            className={`border border-dashed ${clearanceBorderColor} opacity-40 pointer-events-none z-0`}
          />
        </div>

        {!table.isGhost && (
          <div 
            className={`no-drag-table-action absolute -top-2 -right-2 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-orange-500 text-white text-xs sm:text-sm font-bold flex items-center justify-center shadow-md border-2 ${theme === 'dark' ? 'border-hba-mid-purple': 'border-white'} z-30`}
            title={guestTooltip}
          >
            {table.assignedSeats}
          </div>
        )}

        {showRotateButton && (
          <button
            onClick={handleRotateClick}
            className={`no-drag-table-action absolute -bottom-2.5 -right-2.5 p-1.5 rounded-full ${theme === 'dark' ? 'bg-hba-mid-purple text-hba-accent-pink' : 'bg-white text-purple-600'} shadow-lg hover:bg-opacity-80 z-30`}
            aria-label="Rotar mesa"
          >
            <RotateIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        )}
        {showGuestListButton && !table.isGhost && (
          <button
            onClick={handleGuestListClick}
            className={`no-drag-table-action absolute -bottom-2.5 ${showRotateButton ? '-left-2.5' : '-right-2.5'} p-1.5 rounded-full ${theme === 'dark' ? 'bg-hba-mid-purple text-teal-400' : 'bg-white text-teal-600'} shadow-lg hover:bg-opacity-80 z-30`}
            aria-label="Gestionar invitados"
            title="Gestionar invitados"
          >
            <UsersIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        )}
      </div>
    </Draggable>
  );
};

export default TableItem;
